import { recordSsoTrace } from './debug-trace'
import { getFlow } from './flow-store'
import { SSO_STATE_PREFIX } from './types'

/** Kept dependency-light on purpose: this module runs in the `index` entry
 * before the app chunk loads, so it must not pull clients or Amplify along. */
export type CapturedSsoCallback = {
  state: string
  code?: string
  error?: string
  errorDescription?: string
  /** Whether a local flow record exists for this state (same browser). */
  hasFlow: boolean
}

/** OIDC response params that are ours to consume, never journey context values. */
const CALLBACK_PARAMS = [
  'code',
  'state',
  'error',
  'error_description',
  'session_state',
  'iss'
]

let captured: CapturedSsoCallback | undefined
let settled = false

/** A `return_to` is only ever restored on the origin it was captured on. */
const parseSameOriginUrl = (value: string | undefined): URL | undefined => {
  if (!value) return undefined

  try {
    const url = new URL(value)

    return url.origin === window.location.origin ? url : undefined
  } catch {
    return undefined
  }
}

/** Detects an SSO callback at boot (only a `state` with our own prefix counts), captures the
 * OIDC params off the URL and puts the flow record's full original URL back in their place. */
export const captureSsoCallback = (): CapturedSsoCallback | undefined => {
  try {
    const url = new URL(window.location.href)
    const state = url.searchParams.get('state')

    if (!state || !state.startsWith(SSO_STATE_PREFIX)) return undefined

    const flow = getFlow(state)

    const capture: CapturedSsoCallback = {
      state,
      code: url.searchParams.get('code') ?? undefined,
      error: url.searchParams.get('error') ?? undefined,
      errorDescription: url.searchParams.get('error_description') ?? undefined,
      hasFlow: !!flow
    }

    CALLBACK_PARAMS.forEach((param) => url.searchParams.delete(param))

    /** Strip the OIDC params first — same-origin by construction, so this cannot
     * fail and leave them behind to be parsed as journey context values. */
    window.history.replaceState(null, '', url.toString())

    const returnTo = parseSameOriginUrl(flow?.return_to)

    if (returnTo) {
      try {
        window.history.replaceState(null, '', returnTo.toString())
      } catch {
        // Best-effort restore; the stripped URL above already stands
      }
    }

    // Mark captured only once the params are guaranteed off the URL
    captured = capture

    recordSsoTrace({
      phase: 'callback',
      ok: !capture.error && !!capture.code,
      detail: [
        `hasFlow=${capture.hasFlow}`,
        capture.error && `idp_error=${capture.error}`,
        !capture.code && 'no_code'
      ]
        .filter(Boolean)
        .join(' ')
    })

    return captured
  } catch {
    recordSsoTrace({
      phase: 'callback',
      ok: false,
      detail: 'failed to strip the callback params off the URL'
    })

    // A broken capture must never take the whole journey down with it
    return undefined
  }
}

/** The callback captured at boot, if any. */
export const getCapturedSsoCallback = (): CapturedSsoCallback | undefined =>
  captured

/** True from capture until the pipeline settled — restore prompts hold off meanwhile. */
export const hasPendingSsoCallback = (): boolean => !!captured && !settled

/** True forever once a capture was recorded for this page load: settling only
 * clears the pending state, never this fact — the SSO flow owns the session. */
export const wasSsoCallbackCaptured = (): boolean => !!captured

/** Marks the captured callback as dealt with. Must happen BEFORE dispatching a restore:
 * the restore remounts the provider, whose fresh effect would re-enter the pipeline. */
export const markSsoCallbackSettled = (): void => {
  settled = true
}

/** Test-only reset — production code must never rewind the settled guard. */
export const __resetSsoCallbackCaptureForTests = (): void => {
  captured = undefined
  settled = false
}
